// =============================================================================
// App - Root Component con Routing e Provider
// =============================================================================

import { ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ResourceGrid } from './components/scheduler';
import { EditAppointmentModal } from './components/scheduler/EditAppointmentModal';
import { BookingModal, BookingProvider } from './components/scheduler/BookingModal';
import { AppShell } from './components/AppShell';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { SelectTenantPage } from './pages/SelectTenantPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { SettingsPage } from './pages/SettingsPage';
import { ProfilePage } from './pages/ProfilePage';
import { CustomersPage } from './pages/CustomersPage';
import { DashboardPage } from './pages/DashboardPage';
import { ReportsPage } from './pages/ReportsPage';
import { BookingRequestsPage } from './pages/BookingRequestsPage';
import { PublicBookingPage } from './pages/public/PublicBookingPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary, ToastContainer } from './components/common';
import { useBookingPanelMode, useIsModalOpen } from './store/schedulerStore';
import { useTenantStore } from './store/tenantStore';
import { useRealtime } from './hooks/useRealtime';

// React Query client with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minuti
      gcTime: 1000 * 60 * 10, // 10 minuti (formerly cacheTime)
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

/**
 * Aggiornamenti in tempo reale: tiene aperto il socket e traduce gli eventi
 * del server in invalidazioni della cache. Non rende nulla, ma va dentro
 * Router e QueryClientProvider perche' usa entrambi.
 */
function RealtimeBridge() {
  useRealtime();
  return null;
}

/**
 * Layout wrapper per le route protette: Header + contenuto scrollabile
 */
function ProtectedLayout({ children, requiredRoles }: { children: ReactNode; requiredRoles?: Array<'ADMIN' | 'OWNER'> }) {
  return (
    <ProtectedRoute requiredRoles={requiredRoles}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

/**
 * Account personale: unica route protetta che non richiede un'organizzazione
 * attiva. Da select-tenant e onboarding non c'e' ancora un tenant, ma nome,
 * email e password devono restare modificabili: li' la AppShell (sidebar con
 * switcher e navigazione del tenant) non avrebbe nulla da mostrare, quindi la
 * pagina si rende in versione standalone.
 */
function ProfileRoute() {
  const activeTenantId = useTenantStore((s) => s.activeTenantId);
  return (
    <ProtectedRoute requireTenant={false}>
      {activeTenantId ? (
        <AppShell>
          <ProfilePage />
        </AppShell>
      ) : (
        <ProfilePage standalone />
      )}
    </ProtectedRoute>
  );
}

/**
 * Provider condiviso per la prenotazione — si monta quando isModalOpen è true,
 * così lo stato viene resettato a ogni nuova apertura ma è condiviso tra popup e drawer.
 */
function SharedBookingProvider({ children }: { children: ReactNode }) {
  const isOpen = useIsModalOpen();
  if (!isOpen) return <>{children}</>;
  return <BookingProvider>{children}</BookingProvider>;
}

/**
 * Modale di prenotazione globale — visibile solo in modalità popup
 */
function GlobalBookingModal() {
  const panelMode = useBookingPanelMode();
  const isOpen = useIsModalOpen();
  if (panelMode !== 'modal' || !isOpen) return null;
  return <BookingModal />;
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <SharedBookingProvider>
            <RealtimeBridge />
            <div className="h-screen w-screen overflow-hidden bg-gray-50 flex flex-col">
            <Routes>
              {/* Route pubbliche */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/select-tenant" element={<SelectTenantPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />

              {/* Prenotazione pubblica (no auth) */}
              <Route path="/book/:tenantSlug" element={<PublicBookingPage />} />
              
              {/* Route protette */}
              <Route path="/" element={<ProtectedLayout><ResourceGrid /></ProtectedLayout>} />
              <Route path="/scheduler" element={<ProtectedLayout><ResourceGrid /></ProtectedLayout>} />
              <Route path="/dashboard" element={<ProtectedLayout><DashboardPage /></ProtectedLayout>} />
              <Route path="/reports" element={<ProtectedLayout><ReportsPage /></ProtectedLayout>} />
              <Route path="/booking-requests" element={<ProtectedLayout requiredRoles={['ADMIN', 'OWNER']}><BookingRequestsPage /></ProtectedLayout>} />
              <Route path="/settings" element={<ProtectedLayout requiredRoles={['ADMIN', 'OWNER']}><SettingsPage /></ProtectedLayout>} />
              <Route path="/customers" element={<ProtectedLayout><CustomersPage /></ProtectedLayout>} />
              {/* Account personale: nessun ruolo richiesto, riguarda l'utente non l'organizzazione */}
              <Route path="/profile" element={<ProfileRoute />} />
              
              {/* 404 → scheduler */}
              <Route path="*" element={<Navigate to="/scheduler" replace />} />
            </Routes>
          
          {/* Modali globali */}
          <EditAppointmentModal />
          <GlobalBookingModal />
          
          {/* Notifiche */}
          <ToastContainer />
        </div>
          </SharedBookingProvider>
      </BrowserRouter>
      
      {/* React Query DevTools - solo in sviluppo */}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
